import React from 'react';
import { motion } from 'framer-motion';

const TIMELINE = [
  {
    date: 'Inicios',
    icon: '🔧',
    title: 'Del taller al código',
    desc: 'Mi historia no empezó en una universidad sino en talleres. Reparando computadoras, motos y electrónica. Esa escuela práctica de resolución de problemas fue mi base real.',
  },
  {
    date: '2021–2022',
    icon: '🤖',
    title: 'Robótica, Automatización & IoT',
    desc: 'Me gradué en Robótica e IoT, construyendo mis primeros sistemas con ESP32, Arduino y sensores. Descubrí que el software y el hardware son más poderosos juntos.',
  },
  {
    date: '2023–2024',
    icon: '💻',
    title: 'Full Stack Developer',
    desc: 'Me sumergí en el desarrollo web completo: React, Node.js, MongoDB, APIs REST, Google OAuth, Mercado Pago. Empecé a trabajar como freelancer y construir productos reales.',
  },
  {
    date: '2024–2025',
    icon: '🧠',
    title: 'Integración de IA & Agentes',
    desc: 'Incorporé LLMs como Groq (LLaMA), sistemas de agentes de IA y automatización inteligente en proyectos reales. El portafolio que estás viendo corre sobre esta tecnología.',
  },
  {
    date: '2025',
    icon: '🏍️',
    title: 'Proyecto Akira — Alarma IoT inteligente',
    desc: 'Mi proyecto más ambicioso: una alarma para motos con ESP32, sensores múltiples, app móvil y backend en la nube. Hardware + Software + IA en un solo producto.',
  },
];

const SKILLS_MATRIX = [
  { cat: 'Frontend', items: ['React', 'TypeScript', 'Framer Motion', 'Chakra UI', 'Three.js'] },
  { cat: 'Backend', items: ['Node.js', 'Express', 'REST APIs', 'JWT Auth', 'Websockets'] },
  { cat: 'Bases de datos', items: ['MongoDB', 'Mongoose', 'PostgreSQL', 'Redis (básico)'] },
  { cat: 'IA & Agentes', items: ['Groq API', 'LLaMA 3.3 70B', 'Prompt Engineering', 'RAG básico', 'Agentes autónomos'] },
  { cat: 'IoT & Hardware', items: ['ESP32', 'Arduino', 'C++', 'MQTT', 'Sensores & Actuadores'] },
  { cat: 'DevOps & Cloud', items: ['Docker', 'Railway', 'Vercel', 'Render', 'Git'] },
];

const AI_AGENTS_INFO = [
  {
    icon: '🧠',
    title: 'LLMs en producción',
    desc: 'Integración de modelos de lenguaje grande (LLaMA 3.3 70B via Groq) en aplicaciones reales con contexto dinámico, historial de conversación y prompts especializados.',
  },
  {
    icon: '⚡',
    title: 'Velocidad con Groq',
    desc: 'Groq es el hardware de inferencia más rápido del mercado. Respuestas en menos de 1 segundo para LLMs de 70B parámetros. Tu portafolio corre sobre esto.',
  },
  {
    icon: '🔄',
    title: 'Contexto dinámico',
    desc: 'El asistente de este portfolio aprende de los proyectos en tiempo real. Cada vez que se carga un proyecto nuevo, la IA lo incorpora sin reiniciar.',
  },
  {
    icon: '🎯',
    title: 'Agentes especializados',
    desc: 'A diferencia de un chatbot genérico, un agente tiene objetivos, herramientas y contexto específico. El asistente de TinchoDev puede estimar presupuestos, responder técnicamente y agendar reuniones.',
  },
];

export default function About() {

  return (
    <section id="about" style={{ background: '#0f0f0f', padding: '6rem 0' }}>
      <style>{`
        .timeline-line { position:absolute; left:50%; top:0; bottom:0; width:1px; background:linear-gradient(to bottom, transparent, rgba(255,69,0,0.4), transparent); transform:translateX(-50%); }
        @media(max-width:768px) { .timeline-line { left:20px; } .timeline-item-inner { flex-direction:column !important; } }
        .skill-pill { padding:0.35rem 0.8rem; border-radius:20px; font-size:0.78rem; background:rgba(255,69,0,0.08); border:1px solid rgba(255,69,0,0.2); color:rgba(242,242,242,0.7); cursor:default; transition:all 0.2s; }
        .skill-pill:hover { background:rgba(255,69,0,0.2); color:#fff; border-color:rgba(255,69,0,0.5); }
        .ai-card { background:rgba(255,69,0,0.04); border:1px solid rgba(255,69,0,0.15); border-radius:12px; padding:1.5rem; transition:all 0.3s; }
        .ai-card:hover { background:rgba(255,69,0,0.08); border-color:rgba(255,69,0,0.35); transform:translateY(-3px); }
      `}</style>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem' }}>

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Mi historia</p>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#f2f2f2', fontWeight: 800, marginBottom: '1rem' }}>
            Del taller al código,<br />
            <span style={{ color: '#ff4500' }}>del código al futuro</span>
          </h2>
          <p style={{ color: 'rgba(242,242,242,0.5)', maxWidth: '600px', margin: '0 auto', lineHeight: 1.8 }}>
            Desarrollador Full Stack argentino especializado en IoT, Robótica e Inteligencia Artificial.
            Construyo soluciones que conectan el mundo físico con el digital.
          </p>
        </motion.div>

        {/* Timeline */}
        <div style={{ position: 'relative', marginBottom: '5rem' }}>
          <div className="timeline-line" />
          {TIMELINE.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              style={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end', marginBottom: '2.5rem' }}
            >
              <div style={{
                width: 'calc(50% - 40px)', background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,69,0,0.15)', borderRadius: '12px',
                padding: '1.5rem', position: 'relative',
              }}>
                {/* Dot connector */}
                <div style={{
                  position: 'absolute', top: '1.5rem',
                  [i % 2 === 0 ? 'right' : 'left']: '-45px',
                  width: '16px', height: '16px', borderRadius: '50%',
                  background: '#ff4500', border: '3px solid #0f0f0f',
                  boxShadow: '0 0 12px rgba(255,69,0,0.6)',
                }} />
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                <div style={{ color: '#ff4500', fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{item.date}</div>
                <h3 style={{ color: '#f2f2f2', fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.5rem' }}>{item.title}</h3>
                <p style={{ color: 'rgba(242,242,242,0.55)', fontSize: '0.85rem', lineHeight: 1.7 }}>{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* AI Agents Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ marginBottom: '5rem' }}
        >
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>🧠 Inteligencia Artificial</p>
            <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', color: '#f2f2f2', fontWeight: 800 }}>
              Agentes de IA que trabajan por vos
            </h2>
            <p style={{ color: 'rgba(242,242,242,0.5)', maxWidth: '550px', margin: '0.75rem auto 0', fontSize: '0.9rem', lineHeight: 1.7 }}>
              No es solo un chatbot. Los agentes de IA son sistemas autónomos con contexto, memoria y objetivos específicos. Podés verlo en acción en el botón 🤖 abajo a la derecha.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
            {AI_AGENTS_INFO.map((item, i) => (
              <motion.div
                key={i}
                className="ai-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div style={{ fontSize: '1.8rem', marginBottom: '0.75rem' }}>{item.icon}</div>
                <h3 style={{ color: '#f2f2f2', fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem' }}>{item.title}</h3>
                <p style={{ color: 'rgba(242,242,242,0.55)', fontSize: '0.83rem', lineHeight: 1.7 }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Skills Matrix */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Arsenal tecnológico</p>
            <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', color: '#f2f2f2', fontWeight: 800 }}>
              Stack completo, soluciones reales
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {SKILLS_MATRIX.map((group, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                style={{
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '12px', padding: '1.5rem',
                  transition: 'border-color 0.3s',
                }}
                whileHover={{ borderColor: 'rgba(255,69,0,0.3)' }}
              >
                <h3 style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '1rem', fontWeight: 600 }}>{group.cat}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {group.items.map((item) => (
                    <span key={item} className="skill-pill">{item}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
}
